import { useTranslation } from 'react-i18next';
import React from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { useDispatch, useSelector } from 'react-redux';
import { updateNodeData } from '../../../store/workflow/workflowSlice';
import { runStatusLabel, NodeRunStatus, formatDuration } from '../utils/runStatus';
import { typeLabelOf, shapeClassOf } from '../nodeCatalog';
import { TbTrash, TbTable, TbHistory, TbEye, TbPlayerPause, TbPlayerPlay } from 'react-icons/tb';
import styles from './WorkflowNode.module.css';
import { formatRows } from '../../../utils/formatters';

// BaseNode Props 정의
interface BaseNodeProps {
  id: string;
  data: any;
  selected?: boolean;
  type?: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  subtitle?: React.ReactNode;
  handles?: {
    target?: boolean;
    source?: boolean;
    targetPosition?: Position;
    sourcePosition?: Position;
    extraHandles?: Array<{
      id?: string;
      type: 'source' | 'target';
      position: Position;
      className?: string;
      /** 자리·크기는 인라인으로 준다 — CSS 로 옮기면 React Flow 가 잰 자리와 어긋나 선이 핸들 중앙에서 시작하지 않는다 */
      style?: React.CSSProperties;
    }>;
  };
  className?: string;
  isHovered?: boolean;  // 호버 상태 표시 prop 추가
  warning?: string;  // 있으면 노드에 경고 뱃지 표시 (예: 필수 속성 미선택)
  /** 예외 흐름(데이터처리 실패 시 가는 길) 핸들에서 나가는 '+' — 아직 이어진 곳이 없을 때만 (utils/errorFlow) */
  errorStub?: boolean;
}

/**
 * 모든 노드 타입의 기본 구조를 제공하는 BaseNode 컴포넌트
 * 공통 기능: 삭제 기능, 기본 핸들, 스타일링 등
 */
const BaseNode: React.FC<BaseNodeProps> = ({
  id,
  data,
  selected,
  type,
  icon,
  label,
  subtitle,
  handles: handlesProp,
  className = '',
  isHovered = false,  // 기본값 false
  warning,
  errorStub = false,
}) => {
  // 넘겨받은 설정은 기본 핸들(왼쪽 입력·오른쪽 출력)에 **덧붙인다** — 예전에는 통째로 대체해서,
  // 추가 핸들만 주면 정상 흐름 핸들이 사라졌다(현장 2026-09-23: 데이터처리의 예외 흐름).
  const handles = { target: true, source: true, targetPosition: Position.Left, sourcePosition: Position.Right, ...(handlesProp || {}) };

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reactFlowInstance = useReactFlow();
  // 실행 상태 (실행 버튼 → 폴링마다 갱신). 없으면 표시하지 않는다
  const runStatus = useSelector((s: any) => s.workflow?.runStatus?.[id] as NodeRunStatus | undefined);
  // 최종 실행의 소요시간(초) — 태스크로 변환되는 노드만 있다
  const runDuration = useSelector((s: any) => s.workflow?.runDuration?.[id] as number | undefined);
  // 실행 이력 창을 이 노드의 최근 로그에 맞춰 연다 (레이아웃의 openflow:open-node-log 수신부)
  const openRunHistory = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent('openflow:open-node-log', { detail: { nodeId: id } }));
  };
  // 이미 엣지가 붙은 핸들 쪽에는 추가(+) 스텁을 보이지 않는다
  const leftConnected = useSelector((s: any) => (s.workflow?.edges || []).some((e: any) => e.target === id));
  const rightConnected = useSelector((s: any) => (s.workflow?.edges || []).some((e: any) => e.source === id));

  // 노드 삭제 함수
  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    reactFlowInstance.deleteElements({ nodes: [{ id }] });
  };

  // 데이터 노드면 삭제 아이콘 옆에 데이터 보기 아이콘 — 모달은 레이아웃의 DataNodeViewHost 가 띄운다
  const isBranch = String(type) === 'branch';
  //: 워크플로 실행 노드는 가리키는 워크플로가 있을 때만 미리보기를 붙인다 (id 가 비어 있으면 볼 것이 없다)
  const targetWorkflowId = String(type) === 'runWorkflow' ? String(data?.config?.workflowId || '') : '';
  const isRunWorkflow = !!targetWorkflowId;
  const openTargetPreview = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent('openflow:preview-workflow', { detail: { workflowId: targetWorkflowId } }));
  };
  const DATA_NODE_TYPES = ['csvData', 'rdbData', 'innerData'];
  const isDataNode = DATA_NODE_TYPES.includes(String(type));
  // 실행 중단 — 이 노드와 그 뒤는 돌지 않는다(서버가 건너뜀으로 기록). 테라스트림 Designer 의 실행 중단 표시와 같다.
  // 설정은 그대로 두고 표시만 바꾸므로 풀면 원래대로 돈다. 데이터 노드는 태스크가 아니라 해당 없다
  const suspended = !isDataNode && !!data?.config?.suspended;
  const toggleSuspended = (event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(updateNodeData({ id, data: { config: { ...(data?.config || {}), suspended: !suspended } } } as any));
  };
  //: 최근 실행에서 이 노드를 지나간 건수 — 데이터 노드는 상태 대신 이것을 보여 준다
  const runRows = useSelector((s: any) => s.workflow?.runRows?.[id] as number | undefined);
  const handleViewData = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent('openflow:open-node-data', { detail: { nodeId: id } }));
  };

  // 노드 도형은 이 요소 하나로 — React Flow 래퍼(.react-flow__node)는 index.css 에서 투명/패딩 0 으로 만든다.
  // 어떤 도형인지는 툴박스와 **같은 함수**가 정한다 (nodeCatalog.shapeClassOf) — 따로 판단하면 어긋난다.
  const shapeClass = shapeClassOf(type);

  return (
    <div 
      className={`relative shadow-md ${shapeClass} bg-white border-2 ${
        selected ? 'border-blue-500' : isHovered ? 'border-green-400' : 'border-gray-200'
      } ${isHovered ? styles.highlight : ''} ${styles.node} ${runStatus ? `run-${runStatus}` : ''} ${suspended ? 'of-suspended' : ''} px-4 py-2 ${className} ${
        data?.parentDragging ? styles.parentDragging : ''
      }`}
      style={{
        width: '105px',
        height: '56px',
        minWidth: '105px',
        minHeight: '56px'
      }}
    >
      {/* 실행 상태·소요시간은 처리·제어·실행 노드에만 — 데이터 노드는 아래 건수 뱃지를 쓴다 */}
      {runStatus && !isDataNode && (
        <span className={`run-badge run-badge--${runStatus} nodrag`}
          title={`${t('workflow:node.runStatusTitle', { status: runStatusLabel(runStatus) })}${runDuration !== undefined ? t('workflow:node.durationPart', { duration: formatDuration(runDuration) }) : ''}${t('workflow:node.clickForLog')}`}
          onClick={openRunHistory}>
          {runStatus === 'running' && <span className="run-badge__spin" />}
          {runStatusLabel(runStatus)}
          {runDuration !== undefined && runStatus !== 'running' && runStatus !== 'waiting' && (
            <span className="run-badge__dur">{formatDuration(runDuration)}</span>
          )}
        </span>
      )}
      {/* 데이터 노드: 최근 실행에서 이 파일·테이블을 지나간 건수 (실행한 적이 없으면 아무것도 두지 않는다) */}
      {isDataNode && runRows !== undefined && (
        <span className="rows-badge nodrag" title={t('workflow:node.rowsTitle', { rows: formatRows(runRows) })}>
          {formatRows(runRows)}
        </span>
      )}
      {/* 호버 시 좌/우 핸들에서 짧은 선 + '+' — 클릭하면 그 쪽에 추가할 노드 유형 메뉴 (캔버스가 openflow:node-insert 로 받는다) */}
      {(['left', 'right'] as const)
        .filter((side) => (side === 'left' ? !leftConnected : !rightConnected))
        .filter((side) => !(isBranch && side === 'right'))     // 분기의 오른쪽은 아래의 분기 전용 + 가 맡는다
        .map((side) => (
          <div key={side} className={`node-stub node-stub--${side} nodrag`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('openflow:node-insert', { detail: { nodeId: id, side, x: e.clientX, y: e.clientY } })); }}
            title={side === 'left' ? t('workflow:node.addLeft') : t('workflow:node.addRight')}>
            <span className="node-stub__line" />
            <span className="node-stub__plus">+</span>
          </div>
        ))}
      {/* 예외 흐름의 + — 정상 흐름의 '+' 와 같은 장치지만 30° 아래로 뻗고 붉다. 뜻: "실패했을 때 갈 노드를 여기 더한다" */}
      {errorStub && (
        <div className="node-stub node-stub--error nodrag"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('openflow:node-insert', { detail: { nodeId: id, side: 'error', x: e.clientX, y: e.clientY } })); }}
          title={t('workflow:node.addError')}>
          <span className="node-stub__line" />
          <span className="node-stub__plus">+</span>
        </div>
      )}
      {/* 분기 노드의 + — 뜻이 다르므로 모양도 다르다(주황 네모, 갈라지는 선). 이미 후행이 있어도 계속 보인다:
          갈래를 더 여는 것이 분기의 일이라 '이미 이어져 있으니 그만' 이 성립하지 않는다. */}
      {isBranch && (
        <div className="node-stub node-stub--right node-stub--branch nodrag"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('openflow:branch-add', { detail: { nodeId: id } })); }}
          title={t('workflow:node.addBranch')}>
          <span className="node-stub__fork" />
          <span className="node-stub__plus">+</span>
        </div>
      )}
      {/* 노드 외부 우상단 아이콘 — 왼쪽부터 실행 이력, (데이터 노드면) 데이터 보기, 삭제.
          노드를 고르면 테두리가 굵어져 아이콘과 겹치던 자리라 조금 더 위로 띄운다 */}
      <div className="node-tools" style={{ position: 'absolute', top: -24, right: -14, zIndex: 20, display: 'flex', gap: 6 }}>
        {TbHistory({
          className: `${styles.trashIcon} ${styles.dataIcon}`,
          onClick: openRunHistory,
          title: t('workflow:node.historyTitle'),
        })}
        {/* 워크플로 실행 노드: 가리키는 워크플로를 창으로 미리 본다 — 캔버스를 닫고 찾아가지 않아도 되게 */}
        {isRunWorkflow && TbEye({
          className: `${styles.trashIcon} ${styles.dataIcon}`,
          onClick: openTargetPreview,
          title: t('workflow:node.preview'),
        })}
        {DATA_NODE_TYPES.includes(String(type)) && TbTable({
          className: `${styles.trashIcon} ${styles.dataIcon}`,
          onClick: handleViewData,
          title: type === 'innerData' ? t('workflow:node.viewInnerData') : t('workflow:node.viewData'),
        })}
        {!isDataNode && (suspended ? TbPlayerPlay : TbPlayerPause)({
          className: `${styles.trashIcon} ${styles.dataIcon}`,
          onClick: toggleSuspended,
          title: suspended ? t('workflow:node.resume') : t('workflow:node.suspend'),
        })}
        {TbTrash({
          className: styles.trashIcon,
          onClick: handleDelete,
          title: t('workflow:node.delete')
        })}
      </div>
      {/* 실행 중단 표시 — 노드 왼쪽 아래의 주황 ⊠ (테라스트림 Designer 와 같은 자리·색) */}
      {suspended && (
        <span className="of-suspended-badge nodrag" title={t('workflow:node.suspendedTitle')} onClick={toggleSuspended}>⊠</span>
      )}

      {/* 경고 뱃지 - 노드 외부 좌상단에 위치 (예: 데이터 정의 미선택) */}
      {warning && (
        <div
          title={warning}
          style={{ position: 'absolute', top: -10, left: -10, zIndex: 20 }}
          className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[0.6875rem] font-bold shadow"
        >
          !
        </div>
      )}

      {/* 타겟 핸들 (입력) — 위치는 React Flow 기본값에 맡긴다(경계에 반쯤 걸친다).
          예전에 준 transform: translateY(-50%) 가 가로 이동(-50%)까지 지워 핸들이 노드 안으로 들어왔다. */}
      {handles.target && (
        <Handle
          type="target"
          position={handles.targetPosition || Position.Left}
          className="w-4 h-4 border-2 border-white"
        />
      )}

      {/* 노드 안에는 아이콘과 **유형명** 만 — 아이콘 배경은 두지 않는다.
          명칭(사용자가 붙인 이름)은 노드 밖 아래에 둔다: 노드가 좁아 이름이 길면 잘리기 때문이다 */}
      <div className="flex items-center justify-center gap-1.5 w-full px-1">
        <span className="shrink-0 flex items-center justify-center text-gray-600">{icon}</span>
        <span className="min-w-0 flex flex-col items-start">
          <span className="text-xs font-normal text-gray-500 truncate max-w-[60px] leading-tight">{typeLabelOf(type)}</span>
        </span>
      </div>

      {/* 노드 아래: 첫 줄은 명칭, 둘째 줄은 파일명·테이블명(더 작게). 둘 다 줄바꿈 없이, 넘치면 생략 표시.
          엣지 선이 노드 가운데 높이로 지나가므로 충분히 띄우고, 선 위에서도 읽히도록 배경을 깐다 */}
      {(label || subtitle) && (
        <div
          className="nodrag node-caption"
          style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 7,
            maxWidth: 170, textAlign: 'center', pointerEvents: 'none',
          }}
        >
          {label && (
            <div
              className="node-caption__name"
              title={typeof label === 'string' ? label : undefined}
              style={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                // 배경 상자 없이도 연결선 위에서 읽히도록 — 글자를 키우고 진하게, 바탕색 후광만 얇게 두른다
                fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 500,
                color: 'rgb(var(--c-gray-800))',
                textShadow: '0 0 3px rgb(var(--c-gray-50)), 0 0 3px rgb(var(--c-gray-50))',
              }}
            >
              {label}
            </div>
          )}
          {subtitle && (
            <div
              className="node-caption__sub"
              title={typeof subtitle === 'string' ? subtitle : undefined}
              style={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                fontSize: '0.625rem', lineHeight: '0.85rem', color: 'rgb(var(--c-gray-500))',
                textShadow: '0 0 3px rgb(var(--c-gray-50))',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      )}

      {/* 소스 핸들 (출력) */}
      {handles.source && (
        <Handle
          type="source"
          position={handles.sourcePosition || Position.Right}
          className="w-4 h-4 border-2 border-white"
        />
      )}

      {/* 내부 데이터 노드 라벨 표시 */}
      {data?.innerDataLabel && (
        <div className="mt-2 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200 w-full text-center">
          {data.innerDataLabel}
        </div>
      )}

      {/* 추가 핸들 */}
      {handles.extraHandles?.map((handle, index) => (
        <Handle
          key={`extra-handle-${index}`}
          id={handle.id}
          type={handle.type}
          position={handle.position}
          style={handle.style}
          className={handle.className || "w-4 h-4 bg-gray-400 border-2 border-white"}
        />
      ))}
    </div>
  );
};

export default BaseNode; 